import * as z from "zod/v4";

const MINUTES_IN_AN_HOUR = 60;
const MINUTES_IN_A_DAY = 24 * MINUTES_IN_AN_HOUR;
const DAYS_IN_A_WEEK = 7;
const MINUTES_IN_A_WEEK = DAYS_IN_A_WEEK * MINUTES_IN_A_DAY;

type RateSegmentWindow = {
	startDayOfWeek: number;
	numberOfDays: number;
	startHour: number;
	startMinute: number;
	endHour: number;
	endMinute: number;
};

const rateSegmentWeekSpan = (segment: RateSegmentWindow) => ({
	start:
		(segment.startDayOfWeek - 1) * MINUTES_IN_A_DAY + segment.startHour * MINUTES_IN_AN_HOUR + segment.startMinute,
	end:
		(segment.startDayOfWeek - 1 + segment.numberOfDays) * MINUTES_IN_A_DAY +
		segment.endHour * MINUTES_IN_AN_HOUR +
		segment.endMinute,
});

export const rateSegmentEndsAfterStart = (segment: RateSegmentWindow): boolean => {
	const span = rateSegmentWeekSpan(segment);
	return span.end > span.start;
};

export const doRateSegmentsOverlap = ({
	segmentA,
	segmentB,
}: {
	segmentA: RateSegmentWindow;
	segmentB: RateSegmentWindow;
}): boolean => {
	const segmentSpan = rateSegmentWeekSpan(segmentA);
	const otherSpan = rateSegmentWeekSpan(segmentB);

	return [-MINUTES_IN_A_WEEK, 0, MINUTES_IN_A_WEEK].some(
		(shift) => segmentSpan.start < otherSpan.end + shift && otherSpan.start + shift < segmentSpan.end,
	);
};

export const rateTableSchema = z.object({
	segments: z
		.object({
			id: z.number().optional(),
			startDayOfWeek: z.number().int().min(1).max(DAYS_IN_A_WEEK),
			numberOfDays: z.number().int().min(0).max(6),
			startHour: z.number().int().min(0).max(23),
			startMinute: z.number().int().min(0).max(59),
			endHour: z.number().int().min(0).max(23),
			endMinute: z.number().int().min(0).max(59),
			rate: z.number().int().min(0),
			quantity: z.number().int().positive(),
			minimumTotal: z.number().int().min(0).optional(),
			maximumTotal: z.number().int().min(0).optional(),
		})
		.superRefine((segment, ctx) => {
			if (!rateSegmentEndsAfterStart(segment)) {
				ctx.addIssue({ code: "custom", message: "A rate segment must end after it starts", path: ["endHour"] });
			}
			if (
				segment.minimumTotal !== undefined &&
				segment.maximumTotal !== undefined &&
				segment.maximumTotal < segment.minimumTotal
			) {
				ctx.addIssue({
					code: "custom",
					message: "A rate segment's maximum total cannot be below its minimum total",
					path: ["maximumTotal"],
				});
			}
		})
		.array()
		.superRefine((segments: RateSegmentWindow[], ctx: z.RefinementCtx) => {
			segments.forEach((segment, index) => {
				if (
					segments
						.slice(0, index)
						.some((earlier) => doRateSegmentsOverlap({ segmentA: earlier, segmentB: segment }))
				) {
					ctx.addIssue({ code: "custom", message: "Rate segments cannot overlap", path: [index] });
				}
			});
		}),
});

export type RateTableSchema = z.infer<typeof rateTableSchema>;

export type RateSegmentSchema = RateTableSchema["segments"][number];

export const isFlatSegment = (segment: { rate: number; minimumTotal?: number; maximumTotal?: number }) => {
	return segment.rate > 0 && segment.minimumTotal === segment.rate && segment.maximumTotal === segment.rate;
};
